export function resetOptions(element, tomSelect) {
  const value = selectedValue(element)
  const activeValue = tomSelect.activeOption?.dataset?.value

  const { options, optionGroups } = buildOptions(element, tomSelect.settings)

  tomSelect.clear(true)
  tomSelect.clearOptions()
  tomSelect.clearOptionGroups()
  tomSelect.setupOptions(options, optionGroups)
  advanceOrder(tomSelect)

  if (!tomSelect.isOpen) {
    tomSelect.clearActiveOption()
    withoutRendering(tomSelect, () => tomSelect.setValue(value, true))
    return
  }

  tomSelect.setValue(value, true)
  tomSelect.refreshOptions(false)
  tomSelect.lastQuery = null

  if (activeValue) {
    const activeOption = tomSelect.getOption(activeValue)

    if (activeOption) {
      tomSelect.setActiveOption(activeOption)
    }
  }
}

function selectedValue(element) {
  if (element.multiple) {
    return Array.from(element.selectedOptions, (option) => option.value)
  }

  return element.value
}

function withoutRendering(tomSelect, action) {
  const refreshOptions = tomSelect.refreshOptions

  tomSelect.refreshOptions = () => {}

  try {
    action()
  } finally {
    tomSelect.refreshOptions = refreshOptions
  }
}

export function isSynchronized(element, tomSelect) {
  const selectOptions = readSelectOptions(element, tomSelect.settings)
  const tomSelectOptions = readTomSelectOptions(tomSelect)

  if (!optionsEqual(selectOptions, tomSelectOptions)) {
    return false
  }

  const selectedValues = readSelectedValues(element)
  const tomSelectValues = new Set(tomSelect.items.map(String))

  tomSelectValues.delete("")

  return valuesEqual(selectedValues, tomSelectValues)
}

export function advanceOrder(tomSelect) {
  const orders = [
    ...Object.values(tomSelect.options),
    ...Object.values(tomSelect.optgroups)
  ].map((option) => option.$order || 0)

  tomSelect.order = Math.max(tomSelect.order, ...orders)
}

function readSelectOptions(element, settings) {
  const options = []

  Array.from(element.children).forEach((child) => {
    if (child.tagName === "OPTGROUP") {
      optionChildren(child).forEach((option) => {
        options.push(readSelectOption(option, child, settings))
      })
    } else if (child.tagName === "OPTION" && child.value !== "") {
      options.push(readSelectOption(child, null, settings))
    }
  })

  return options
}

function readSelectOption(option, optionGroup, settings) {
  const { value, textContent: label, disabled } = option

  const dataset = serializeDataset(option.dataset, settings)

  if (optionGroup) {
    return [value, label, disabled, optionGroup.label, optionGroup.disabled, dataset]
  } else {
    return [value, label, disabled, null, false, dataset]
  }
}

function readTomSelectOptions(tomSelect) {
  const settings = tomSelect.settings

  const options = []

  Object.values(tomSelect.options).
    sort((option, otherOption) => option.$order - otherOption.$order).
    forEach((option) => {
      const {
        [settings.valueField]: value,
        [settings.labelField]: label,
        [settings.disabledField]: disabled,
        [settings.optgroupField]: optionGroupValue
      } = option

      const optionValue = String(value)
      const dataset = serializeDataset(option, settings)
      const optionGroup = tomSelect.optgroups[optionGroupValue]

      if (optionGroup) {
        const {
          [settings.optgroupLabelField]: optionGroupLabel,
          [settings.disabledField]: optionGroupDisabled
        } = optionGroup

        options.push([optionValue, label, !!disabled, optionGroupLabel, !!optionGroupDisabled, dataset])
      } else if (optionValue !== "") {
        options.push([optionValue, label, !!disabled, null, false, dataset])
      }
    })

  return options
}

function serializeDataset(data, settings) {
  const replacedKeys = [
    settings.valueField,
    settings.labelField,
    settings.disabledField,
    settings.optgroupField
  ]

  const keys = []

  for (const key in data) {
    if (!key.startsWith("$") && !replacedKeys.includes(key)) {
      keys.push(key)
    }
  }

  if (keys.length === 0) {
    return ""
  }

  keys.sort()

  return JSON.stringify(keys.map((key) => [key, data[key]]))
}

function optionChildren(element) {
  return Array.from(element.children).filter((child) => child.tagName === "OPTION")
}

function readSelectedValues(element) {
  const values = new Set(Array.from(element.selectedOptions, (option) => option.value))

  values.delete("")

  return values
}

function optionsEqual(options, otherOptions) {
  if (options.length !== otherOptions.length) {
    return false
  }

  return options.every((option, index) => {
    const otherOption = otherOptions[index]

    return option.every((field, fieldIndex) => field === otherOption[fieldIndex])
  })
}

function valuesEqual(values, otherValues) {
  if (values.size !== otherValues.size) {
    return false
  }

  for (const value of values) {
    if (!otherValues.has(value)) {
      return false
    }
  }

  return true
}

function buildOptions(element, tomSelectSettings) {
  const options = []
  const optionGroups = []

  let index = 1
  let order = 1
  Array.from(element.children).forEach((child) => {
    const tagName = child.tagName

    if (tagName === "OPTGROUP") {
      const optgroup = child

      optionGroups.push({
        [tomSelectSettings.optgroupValueField]: index,
        [tomSelectSettings.optgroupLabelField]: optgroup.label,
        [tomSelectSettings.disabledField]: optgroup.disabled
      })

      optionChildren(optgroup).forEach((option) => {
        options.push(buildOption(option, order, index, tomSelectSettings))

        order++
      })

      index++
    } else if (tagName === "OPTION") {
      const option = child

      if (option.value !== "") {
        options.push(buildOption(option, order, null, tomSelectSettings))

        order++
      }
    }
  })

  return {
    options,
    optionGroups
  }
}

function buildOption(option, order, optionGroupIndex, tomSelectSettings) {
  const data = {
    ...option.dataset,
    [tomSelectSettings.valueField]: option.value,
    [tomSelectSettings.labelField]: option.textContent,
    [tomSelectSettings.disabledField]: option.disabled,
    $option: option,
    $order: order
  }

  if (optionGroupIndex !== null) {
    data[tomSelectSettings.optgroupField] = optionGroupIndex
  }

  return data
}
